import {
  DepositIcon,
  MoreOne,
  TradeIcon,
  WithdrawIcon,
} from "assets/Icon";
import { Grid, IconButton, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import Coin from "./Coin";
import CoinCardWithColumn from "./CoinCardWithColumn";
import CoinCardWithRow from "./CoinCardWithRow";
import NumberFormats from "components/NumberFormats";
import Table from "components/Table";
import TableSkeleton from "components/Skeletons/Table.skeleton";
import { TextCell } from "./TextCell";
import WalletActions from "components/WalletActions";
import useIsMobileView from "hooks/useIsMobileView";
import useDispatch from "hooks/useDispatch";
import { getBasketWalletList, getPortfolioWalletList } from "store/slices/wallet/walletSlice";
import { useSelector } from "react-redux";
import Switch from "components/Switch";
import ToggleView from "./ToggleView";
//   import { VALUE_BALANCE } from "pages/Wallet/constants";

const headers = [
  {
    key: "coin",
    align: "left",
    name: "Coin",
    sortable: true,
    sortKey: "coinName",
  },
  {
    key: "total",
    align: "left",
    name: "Total",
    sortable: true,
    sortKey: "totalBalance",
  },
  {
    key: "available",
    align: "left",
    name: "Available",
    sortable: true,
    sortKey: "availableBalance",
  },
  {
    key: "limit",
    align: "left",
    name: "Limit",
    sortable: true,
    sortKey: "tradeLimit",
  },
  {
    key: "borrowed",
    align: "left",
    name: "Borrowed",
    sortable: true,
    sortKey: "borrowed",
  },
  {
    key: "interest",
    align: "left",
    name: "Interest",
    sortable: true,
    sortKey: "interest",
  },
  {
    key: "equity",
    align: "left",
    name: "Equity / Equity Value Blnc",
    sortable: true,
    sortKey: "equityValue",
  },
  {
    key: "actions",
    align: "center",
    name: "",
  },
];

const coinHeaders = [
  {
    key: "totalBalance",
    bold: true,
    text: true,
    title: "Total",
  },
  {
    key: "tradeLimit",
    text: true,
    bold: true,
    title: "Limit",
  },
  {
    key: "availableBalance",
    bold: true,
    text: true,
    title: "Available",
  },
  {
    key: "tradeLimit",
    bold: true,
    text: true,
    title: "Limit",
  },
  {
    key: "borrowed",
    bold: true,
    text: true,
    title: "Borrowed",
  },
  {
    key: "interest",
    bold: true,
    text: true,
    title: "Interest",
  },
  {
    key: "equityValue",
    bold: true,
    text: true,
    title: "Equity",
  },
  {
    key: "equityValueBaseCoin",
    bold: true,
    text: true,
    title: "Equity Value Blnc",
  },
]

export function WalletTable({ isBasket }) {
  const dispatch = useDispatch()
  const coinList = useSelector((state) => state.wallet.portfolioWalletList?.data)

  const isMobileView = useIsMobileView();
  const theme = useTheme();

  const navigate = useNavigate();
  const { id, portfolioId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const isShowMore = searchParams.get('isShowMore') === "true";

  const [loading, setLoading] = useState(false);
  const [activeView, setActiveView] = useState("list");
  const [showWalletAction, setShowWalletAction] = useState(false);
  const [selectedCoin, setSelectedCoin] = useState(null);

  const handleSetShowMore = (e) => {
    const checked = e.target.checked

    const allSearchParams = Object.fromEntries([...searchParams]);

    setSearchParams({
      ...allSearchParams,
      isShowMore: checked,
    });

  }

  const handleShowWalletAction = (data) => {
    setSelectedCoin(data);
    setShowWalletAction(true);
  };

  const walletActionItems = [
    {
      icon: (
        <TradeIcon
          color={theme.palette.secondary.main}
          width={"24px"}
          height={"24px"}
        />
      ),
      text: "Trade",
      onClick: (data) => {
        const USDT = 3;
        const Toman = 4;

        const url =
          data.coinId === USDT || data.coinId === Toman
            ? `/trade/${USDT}/${Toman}`
            : `/trade/${data.coinId}/${USDT}`;

        navigate(url);
      },
    },
    {
      icon: (
        <WithdrawIcon
          color={theme.palette.secondary.main}
          width={"24px"}
          height={"24px"}
        />
      ),
      text: "Borrow",
      onClick: (data) => {
        setSearchParams({
          borrow: data.coinId,
          basketId: data.basketOwnerId
        });
      },
    },
    {
      icon: (
        <DepositIcon
          color={theme.palette.secondary.main}
          width={"24px"}
          height={"24px"}
        />
      ),
      text: "Repay",
      onClick: (data) => {
        setSearchParams({
          repay: data.coinId,
          basketId: data.basketOwnerId
        });
      },
    },
  ];

  const formattedCoinList = coinList?.map((data) => {
    return {
      data: data,

      coin: <Coin coin={data} />,

      borrowed: <TextCell
        item={<NumberFormats value={data?.borrowed} />}
        component="p"
      />,

      interest: <TextCell
        item={<NumberFormats value={data?.interest} />}
        component="p"
      />,

      equity: (
        <>
          <TextCell
            item={<NumberFormats value={data?.equityValue} />}
            component="span"
          /> / {data?.equityValueBaseCoin} {data?.coinName}
        </>
      ),

      available: (
        <>
          <TextCell
            item={<NumberFormats value={data?.availableBalance} />}
            component="p"
          />
          <TextCell color="secondary" item={data?.availableBalanceComment} />
        </>
      ),

      limit: (
        <>
          <TextCell item={data?.tradeLimit} component="p" />
          <TextCell color="secondary" item={data?.tradeLimitComment} />
        </>
      ),

      valueBalance: (
        <>
          <Typography component="span" color={"text.main"} variant="h6">
            <NumberFormats
              value={data?.totalBalanceBaseCoin}
            />
            <Typography
              variant="subtitle1"
              component="span"
              color={"text.secondary"}
              pl={1}
            >
              {data?.baseCoinName}
            </Typography>
          </Typography>
        </>
      ),

      actions: (
        <>
          {!data?.portfolioOwnerId &&
            <IconButton color="text" onClick={() => handleShowWalletAction(data)}>
              <MoreOne color={theme.palette.text.main} />
            </IconButton>
          }
        </>
      ),
    };
  });

  const handleGetPortfolioWalletList = async () => {
    setLoading(true)

    if (isBasket) {
      await dispatch(getBasketWalletList({ id, isShowMore, portfolioId }))
    } else {
      await dispatch(getPortfolioWalletList({ id, isShowMore }))
    }

    setLoading(false)
  }

  useEffect(() => {
    handleGetPortfolioWalletList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowMore, id, portfolioId])

  return (
    <>
      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={8} display="flex" justifyContent="flex-start">
          <Switch
            checked={isShowMore}
            onChange={handleSetShowMore}
            label="Show More"
            color="secondary"
          />
        </Grid>

        {!isMobileView && (
          <Grid textAlign="right" item sm={4}>
            <ToggleView onChange={setActiveView} value={activeView} />
          </Grid>
        )}
      </Grid>


      {isMobileView ? (
        <>
          {coinList?.map((coin) => (
            <CoinCardWithRow
              coin={coin}
              headers={coinHeaders}
              onClick={(coin) => {
                const USDT = 3;
                const Toman = 4;

                const url =
                  coin.coinId === USDT || coin.coinId === Toman
                    ? `/trade/${USDT}/${Toman}`
                    : `/trade/${coin.coinId}/${USDT}`;

                navigate(url);
              }}
            />
          ))}
        </>
      ) : (
        <>
          {activeView === "list" ? (
            <>
              {!loading ? (
                <Table headers={headers} items={formattedCoinList} />
              ) : (
                <TableSkeleton />
              )}
            </>
          ) : (
            <Grid container spacing={1.25}>
              {coinList?.map((coin) => (
                <Grid item lg={3} xs={6} sm={6} md={4} key={coin?.id}>
                  <CoinCardWithColumn
                    coin={coin}
                    headers={coinHeaders}
                    onAction={(data) => handleShowWalletAction(data)}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </>
      )}

      <WalletActions
        hideBookmark
        items={walletActionItems}
        open={showWalletAction}
        coin={selectedCoin}
        onClose={() => {
          setShowWalletAction(false);
        }}
      />
    </>
  );
}

export default WalletTable;
